import type { ServiceSection } from "@/components/ServiceSections";

export interface CityContent {
  headline: string;
  headlineAccent: string;
  introParagraph: string;
  localChallenges: { heading: string; items: string[] };
  whyChooseUs: string[];
  uniqueSection?: ServiceSection;
  seoTitle?: string;
  seoDescription?: string;
}

export const cityContent: Record<string, CityContent> = {
  "tolleson-az": {
    headline: "Your Tolleson Plumber —",
    headlineAccent: "Veteran-Owned, Locally Based",
    introParagraph:
      "Tru American Plumbing is headquartered right here in Tolleson, 85353 — near the 91st Ave corridor where we've served our neighbors for years. We know Tolleson's mix of original neighborhood homes with galvanized pipes and aging sewer lines alongside newer developments near 99th Avenue. As the only veteran-owned plumber based in Tolleson itself, we offer the fastest response times in the area — often same-day for non-emergency calls, and within the hour for emergencies.",
    localChallenges: {
      heading: "Plumbing Challenges Specific to Tolleson",
      items: [
        "Aging galvanized pipes in original Tolleson homes (1960s–1980s) causing rust, low pressure, and frequent leaks",
        "High Total Dissolved Solids (TDS) in municipal water — typically 500–700 ppm — affecting taste and damaging fixtures",
        "Slab movement from expansive clay soils causing foundation shifts and slab leak risk, especially during dry-to-wet seasonal transitions",
        "Older clay tile sewer lines prone to root intrusion from mature neighborhood trees",
      ],
    },
    whyChooseUs: [
      "Headquartered in Tolleson — fastest response times, not driving in from Phoenix or Scottsdale",
      "We've worked on homes in every Tolleson neighborhood, from the original grid streets to newer builds near I-10",
      "Veteran-owned family business — accountability and integrity aren't marketing slogans, they're how we operate",
      "Same-day availability for most service calls because we're already local",
      "We understand Tolleson's specific water quality and infrastructure challenges firsthand",
    ],
    uniqueSection: {
      type: "tip",
      heading: "Headquartered Here — What That Means for You",
      text: "Unlike plumbing companies that list Tolleson as a 'service area' but dispatch from 30 miles away, we're headquartered right here in your community. When you call, a technician can often be at your door within the hour for emergencies. For scheduled service, we offer same-day appointments that remote competitors can't match. Your call goes to a local team, not a Phoenix call center.",
    },
  },

  "avondale-az": {
    headline: "Avondale Plumber —",
    headlineAccent: "Serving Homes Near I-10 & Dysart",
    introParagraph:
      "Avondale has exploded with growth over the past two decades — Crystal Gardens, Garden Lakes, Coldwater Springs, and new developments along the I-10 corridor have transformed the city. But rapid growth brings plumbing challenges. We serve both the newer construction where warranty-period issues surface and the established Avondale neighborhoods near Dysart and Western where 20-year-old plumbing is starting to show its age.",
    localChallenges: {
      heading: "Avondale's Unique Plumbing Challenges",
      items: [
        "New-build homes developing warranty-period plumbing issues — improperly soldered joints, construction debris in lines, and undertested systems",
        "Arizona hard water destroying brand-new fixtures within 3–5 years of installation in newer communities",
        "HOA compliance requirements for exterior plumbing modifications, water softener installations, and visible equipment",
        "Established neighborhoods (20+ year homes) experiencing water heater failures, PRV degradation, and early sewer line issues",
      ],
    },
    whyChooseUs: [
      "Just minutes from Avondale via I-10 — fast response from our Tolleson headquarters",
      "Experience with both new-construction punch list items and aging system repairs",
      "We work with Avondale HOAs on compliant installations for water treatment equipment",
      "Knowledge of Avondale's specific water supply and pressure zones",
      "Trusted by Avondale families in Crystal Gardens, Garden Lakes, and surrounding communities",
    ],
    uniqueSection: {
      type: "stats",
      items: [
        { value: "80K+", label: "Avondale residents served" },
        { value: "3–5 yr", label: "When new-build plumbing issues typically appear" },
        { value: "15 min", label: "Our typical drive time from Tolleson" },
        { value: "20+ gpg", label: "Water hardness in some Avondale zones" },
      ],
    },
  },

  "goodyear-az": {
    headline: "Goodyear, AZ Plumber —",
    headlineAccent: "From Estrella to Palm Valley",
    introParagraph:
      "Goodyear's master-planned communities — Estrella, Palm Valley, Canyon Trails, PebbleCreek — are some of the most desirable neighborhoods in the West Valley. These larger homes with multiple bathrooms, pools, and extensive landscaping put serious demand on plumbing systems. We serve homeowners across Goodyear who need a plumber who understands the specific challenges of large-lot, high-demand residential plumbing.",
    localChallenges: {
      heading: "Goodyear Plumbing Challenges",
      items: [
        "Long sewer line runs on large lots — more pipe length means more opportunity for root intrusion and settling",
        "Water pressure drops in elevated areas of Estrella and Canyon Trails where homes sit above the main distribution system",
        "High water usage from pools, spa equipment, and drip irrigation systems creating extra load on plumbing infrastructure",
        "Multi-bathroom homes (3–5 bathrooms) requiring recirculating pump systems for consistent hot water delivery",
      ],
    },
    whyChooseUs: [
      "Familiar with Goodyear's master-planned communities — Estrella, Palm Valley, PebbleCreek, Canyon Trails",
      "Experience with large-home plumbing systems including recirculating pumps and pool equipment connections",
      "Understanding of Goodyear's water pressure zones and elevation-related challenges",
      "Service larger lot homes where sewer lines run 100+ feet and require specialized attention",
      "Responsive scheduling that works with busy Goodyear families",
    ],
    uniqueSection: {
      type: "prose",
      heading: "Neighborhoods We Serve in Goodyear",
      paragraphs: [
        "From the hilltop homes of Estrella Mountain Ranch to the family neighborhoods of Palm Valley and the active-adult community at PebbleCreek, we've worked in every major Goodyear development. Canyon Trails, Centerra, Sarival — each community has its own builder, its own pipe materials, and its own water pressure profile.",
        "When you call, you're talking to a team that's already been inside homes in your neighborhood. We know which builders used PEX, which used copper, and which developments have the most hard water damage. That knowledge means faster diagnosis and smarter repairs.",
      ],
    },
  },

  "glendale-az": {
    headline: "Plumbing Services in Glendale, AZ —",
    headlineAccent: "Historic to Modern",
    introParagraph:
      "Glendale is a city of contrasts — the charming bungalows near Historic Catlin Court with 1920s–1940s plumbing systems sit just miles from the modern entertainment district surrounding State Farm Stadium and Westgate. We handle both worlds: replacing galvanized pipes in Old Glendale's craftsman homes and servicing the contemporary systems in Glendale's newer developments near Arrowhead and the Loop 101.",
    localChallenges: {
      heading: "Glendale's Plumbing Challenges",
      items: [
        "Galvanized pipe replacement in homes near Historic Downtown — these 60–100 year-old pipes are corroded inside, reducing pressure and discoloring water",
        "Polybutylene (poly-B) pipe concerns in 1980s–1990s Glendale homes — this pipe material is failure-prone and many insurers won't cover it",
        "Pre-game and event emergency demand near Westgate and State Farm Stadium — high-traffic days increase strain on area plumbing and sewer systems",
        "Mixed-era neighborhoods where 1950s ranch homes sit next to 2010s infill — requiring versatility in pipe materials and repair approaches",
      ],
    },
    whyChooseUs: [
      "Experience with Glendale's full range — from 1920s bungalows to brand-new builds",
      "Galvanized-to-copper and copper-to-PEX repipe specialists",
      "Familiar with polybutylene pipe identification, assessment, and replacement options",
      "Quick response for Glendale and Westgate area — just 10 minutes from our Tolleson base",
      "Serving homes in Historic Glendale, Arrowhead Ranch, Thunderbird, and everywhere in between",
    ],
    uniqueSection: {
      type: "comparison",
      heading: "Old Glendale vs. New Glendale Plumbing",
      optionA: {
        title: "Historic Glendale (pre-1970)",
        points: [
          "Galvanized steel pipes corroded from decades of hard water",
          "Cast iron drain lines that may be rusting through",
          "Smaller pipe diameters by today's standards",
          "Often no water softener or treatment",
          "Sewer lines may be original clay tile or Orangeburg",
        ],
      },
      optionB: {
        title: "Modern Glendale (post-2000)",
        points: [
          "Copper or PEX supply lines in good condition",
          "PVC drain lines with longer lifespan",
          "Modern fixtures but still affected by hard water",
          "Builder-grade water heaters nearing end of life (if 15+ years)",
          "ABS or PVC sewer lines less prone to root intrusion",
        ],
      },
    },
  },

  "peoria-az": {
    headline: "Peoria, AZ Plumber —",
    headlineAccent: "Trusted Service from Vistancia to Old Town",
    introParagraph:
      "Peoria stretches from the walkable streets of Old Town Peoria near the spring training stadiums all the way north to the master-planned community of Vistancia and the 55+ neighborhoods of Sun City and Sun City West. That geographic spread means wildly different plumbing needs — and we cover all of them. Whether you're in a 1,200 sq ft Old Town ranch or a 3,500 sq ft Vistancia two-story, we know Peoria plumbing.",
    localChallenges: {
      heading: "Peoria Plumbing Challenges",
      items: [
        "Long water supply runs in spread-out developments like Vistancia — longer pipe runs mean more pressure loss and wait time for hot water",
        "Recirculating pump needs in large Peoria homes — 3,000+ sq ft homes with multiple bathrooms waste gallons waiting for hot water without one",
        "55+ community-specific needs — accessible fixture installation, comfort-height toilets, lever handles, and anti-scald devices",
        "Hard water damage accelerated by Peoria's municipal water profile, particularly in northern zones near Lake Pleasant",
      ],
    },
    whyChooseUs: [
      "We serve all of Peoria — from Old Town to Vistancia, Westwing, Sunrise, and the 55+ communities",
      "Specialists in recirculating pump systems for large homes and long pipe runs",
      "Experience with 55+ community plumbing needs including ADA-compliant fixture installation",
      "Understanding of Peoria's water pressure zones across its large geographic footprint",
      "Trusted by Peoria families and retirees alike for honest, reliable service",
    ],
    uniqueSection: {
      type: "tip",
      heading: "55+ Community Plumbing Expertise",
      text: "Sun City, Sun City West, and Peoria's active-adult communities have specific plumbing needs. We install comfort-height toilets, lever-handle faucets for easier grip, anti-scald shower valves for safety, and accessible fixtures that meet ADA guidelines. We also understand the unique plumbing systems in these communities — many have shared irrigation systems, specific water softener requirements, and HOA guidelines we can navigate.",
    },
  },

  "surprise-az": {
    headline: "Surprise, AZ Plumber —",
    headlineAccent: "Rapid Growth, Reliable Plumbing",
    introParagraph:
      "Surprise has been one of Arizona's fastest-growing cities for over a decade, with communities like Sun City Grand, Marley Park, Asante, and Prasada transforming former farmland into thriving neighborhoods. That rapid growth means thousands of homes hitting their 5–15 year plumbing milestones simultaneously — the age when builder-grade water heaters fail, hard water damage shows up, and original fixtures start leaking.",
    localChallenges: {
      heading: "Surprise Plumbing Challenges",
      items: [
        "Construction-defect plumbing in rapidly-built homes — improperly graded drain lines, inadequate pipe support, and rushed solder joints surfacing after warranty expires",
        "Water pressure inconsistency at the city's expanding edges where new infrastructure connects to existing mains",
        "Desert landscaping irrigation cross-connections — improperly installed drip systems can create backflow contamination risks",
        "Builder-grade water heaters (6–8 year models) reaching end-of-life across entire neighborhoods simultaneously",
      ],
    },
    whyChooseUs: [
      "Understanding of Surprise's rapid-build construction methods and common defects that appear after 5–10 years",
      "Experience in Sun City Grand, Marley Park, Asante, Prasada, and surrounding Surprise communities",
      "New-homeowner plumbing inspections to catch issues before warranty expires",
      "Water treatment systems sized for Surprise's specific water profile and pressure zones",
      "Reliable scheduling for a growing city that deserves plumbers who show up on time",
    ],
    uniqueSection: {
      type: "process",
      heading: "New-Homeowner Plumbing Inspection",
      steps: [
        {
          title: "Full System Check",
          desc: "We inspect every fixture, water line, drain, and water heater in your home — looking for construction defects, improper installations, and early signs of Arizona hard water damage.",
        },
        {
          title: "Water Quality Test",
          desc: "A comprehensive water test measures hardness, TDS, pH, and chlorine levels to determine what treatment your Surprise home needs to protect pipes and appliances.",
        },
        {
          title: "Report & Recommendations",
          desc: "You receive a written report of findings with prioritized recommendations — what needs attention now, what to monitor, and what can wait. No pressure, just honest assessment.",
        },
      ],
    },
  },

  "phoenix-az": {
    headline: "West Phoenix Plumber —",
    headlineAccent: "Serving Maryvale, Laveen & Beyond",
    introParagraph:
      "We don't try to be the plumber for all of Phoenix — we focus on the west side where we can provide fast, personal service. Maryvale, Laveen, Estrella Village, and the neighborhoods along the I-10 west corridor are our territory. This part of Phoenix has a fascinating mix of 1950s ranch homes with original copper plumbing, 1980s tract homes, and brand-new infill development — each era with its own plumbing personality.",
    localChallenges: {
      heading: "West Phoenix Plumbing Challenges",
      items: [
        "Mixed-era plumbing — 1950s ranch homes with copper joints that have lasted 70 years sit next to 1980s homes with failing polybutylene and 2020s infill with modern PEX",
        "City of Phoenix water main pressure variations across west side neighborhoods, causing PRV failures and inconsistent pressure",
        "Monsoon-season flood damage to sewer lines and cleanouts — West Phoenix's flood-prone areas see seasonal backup spikes every July through September",
        "Aging infrastructure in established Maryvale neighborhoods where cast iron drain lines and galvanized supply lines are original to the home",
      ],
    },
    whyChooseUs: [
      "Focused on West Phoenix — Maryvale, Laveen, Estrella Village, and the I-10 west corridor",
      "We know every era of West Phoenix construction and the plumbing that came with it",
      "Fast response from Tolleson — we're already on your side of town",
      "Experience with City of Phoenix permit requirements and inspection processes",
      "Bilingual service available for Maryvale and surrounding communities",
    ],
    uniqueSection: {
      type: "prose",
      heading: "Our West Phoenix Coverage Area",
      paragraphs: [
        "We focus our Phoenix service on the neighborhoods west of I-17 and along the I-10 corridor — from 35th Avenue out to the Estrella area, and from Camelback south through Laveen. This includes Maryvale, Estrella Village, historic South Phoenix neighborhoods, and the new Laveen developments south of Baseline.",
        "This focus means we're never more than 20 minutes away. When a Maryvale homeowner has a sewer backup during monsoon season or a Laveen family loses hot water, we're close enough to respond quickly instead of fighting cross-town Phoenix traffic. It's a deliberate choice — serve fewer areas better, rather than promise coverage we can't back up with fast response.",
      ],
    },
  },

  "buckeye-az": {
    headline: "Buckeye, AZ Plumber —",
    headlineAccent: "From Verrado to Sundance",
    introParagraph:
      "Buckeye is Arizona's fastest-growing city — and that growth corridor from Verrado through Sundance, Tartesso, and Festival Ranch is transforming the landscape. But Buckeye isn't all new construction. Older areas near Downtown Buckeye and unincorporated zones still rely on well water, and some rural properties face plumbing challenges unlike anything in the rest of the metro area. We handle both worlds.",
    localChallenges: {
      heading: "Buckeye Plumbing Challenges",
      items: [
        "Well water in unincorporated areas — high hardness (sometimes 30+ gpg), iron staining, and sulfur odor require specialized treatment systems",
        "Long distances from water mains to homes in rural lots — extended pipe runs are vulnerable to extreme heat damage and pressure loss",
        "New-construction plumbing punch lists in rapidly-developed communities where builders rush to keep up with demand",
        "Extreme heat effects on exposed pipe runs and outdoor equipment — summer surface temperatures above 150°F stress PVC, PRVs, and hose bibs",
      ],
    },
    whyChooseUs: [
      "Experience with both Buckeye municipal water and private well systems",
      "Serve Verrado, Sundance, Tartesso, Festival Ranch, and rural Buckeye properties",
      "Well water treatment specialists — softeners, iron filters, and whole-home systems sized for well water challenges",
      "Understanding of Buckeye's unique mix of new construction and older rural plumbing",
      "We make the drive to Buckeye regularly — it's a core service area, not an afterthought",
    ],
    uniqueSection: {
      type: "comparison",
      heading: "Well Water vs. Municipal Water in Buckeye",
      optionA: {
        title: "Municipal Water (City of Buckeye)",
        points: [
          "Treated and chlorinated at city facilities",
          "Consistent pressure from municipal mains",
          "Hardness typically 15–20 gpg — still very hard",
          "Water softener recommended for pipe protection",
          "Standard whole-home treatment approach works well",
        ],
      },
      optionB: {
        title: "Well Water (Unincorporated Areas)",
        points: [
          "Untreated — hardness can exceed 30 gpg",
          "May contain iron, sulfur, or sediment",
          "Pressure depends on well pump and pressure tank condition",
          "Requires comprehensive testing before treatment design",
          "Often needs softener + iron filter + sediment pre-filter",
        ],
      },
    },
  },

  "litchfield-park-az": {
    headline: "Litchfield Park Plumber —",
    headlineAccent: "Small-Town Character, Expert Service",
    introParagraph:
      "Litchfield Park is one of the West Valley's most distinctive communities — a small, tight-knit city built around the historic Wigwam resort with tree-lined streets, mature landscaping, and homes ranging from original 1940s–1960s ranch houses to newer custom builds in communities like Village at Litchfield Park and Russell Ranch. That blend of historic charm and modern living creates specific plumbing demands. We serve Litchfield Park homeowners who value quality workmanship that respects their property's character.",
    localChallenges: {
      heading: "Litchfield Park Plumbing Challenges",
      items: [
        "Aging copper supply lines in homes near The Wigwam and original Litchfield Park neighborhoods — 50–70 year-old copper develops pinhole leaks from decades of hard water exposure",
        "Strict HOA and historic-district aesthetic requirements that limit visible plumbing equipment placement, exterior pipe routing, and water treatment system installation locations",
        "Hard water damage on high-end fixtures — Litchfield Park homes often feature premium faucets, showerheads, and appliances that are especially vulnerable to mineral buildup and etching",
        "Limited lot access for sewer line work — mature trees, established landscaping, and narrow side yards in older sections make trenchless methods the preferred approach",
      ],
    },
    whyChooseUs: [
      "Just minutes from Litchfield Park via Litchfield Road — fast response from our Tolleson base",
      "Experience working within HOA guidelines and historic-area aesthetic requirements for equipment placement",
      "Careful workmanship that respects established landscaping and mature property features",
      "Premium fixture expertise — we service and install high-end brands found in Litchfield Park homes",
      "Trenchless sewer repair capabilities for properties where traditional trenching isn't practical",
    ],
    uniqueSection: {
      type: "tip",
      heading: "HOA-Compliant Plumbing in Litchfield Park",
      text: "Litchfield Park's HOAs and community guidelines often restrict where water softeners, filtration systems, and exterior plumbing equipment can be installed. We've worked with multiple Litchfield Park HOAs and understand common requirements — equipment must be screened from view, installations can't alter exterior aesthetics, and noise from recirculating pumps or treatment systems must stay within limits. We handle the planning and placement so your installation passes HOA review the first time, avoiding costly modifications or removal orders.",
    },
  },

  "sun-city-az": {
    headline: "Plumber in Sun City, AZ —",
    headlineAccent: "55+ Community Plumbing Specialists",
    introParagraph:
      "Looking for a reliable plumber in Sun City, AZ? Sun City is one of America's original active-adult communities, and its homes reflect that pioneering history. Built primarily between 1960 and 1978, Sun City's housing stock includes some of the oldest residential plumbing in the West Valley — original galvanized supply lines, aging copper systems, and cast iron drain pipes that have been working for over half a century. Plumbers in Sun City AZ need to understand both the community's unique slab-on-grade infrastructure and the accessibility needs of its residents — and that's exactly what we do every day. From Bell Road down to Olive Avenue and across all 7 of Sun City's recreation-center neighborhoods, we provide honest, senior-friendly plumbing service.",
    localChallenges: {
      heading: "Sun City Plumbing Challenges",
      items: [
        "Original galvanized and copper supply lines from the 1960s–1970s — corroded interiors reduce pressure and discolor water, and many are past their expected lifespan",
        "Cast iron and clay sewer lines that have endured 50+ years of use — root intrusion, joint separation, and interior scaling are common findings during camera inspections",
        "Accessibility requirements for aging-in-place residents — comfort-height toilets, lever-handle faucets, anti-scald valves, and grab-bar-compatible shower valve placement",
        "Recreation center and shared facility connections — some Sun City properties tie into community irrigation and shared water infrastructure with unique maintenance considerations",
      ],
    },
    whyChooseUs: [
      "Deep experience with 1960s–1970s construction methods and materials found throughout Sun City",
      "Galvanized-to-copper and copper-to-PEX repipe specialists — we've repiped hundreds of homes of this era",
      "Senior-friendly service — clear communication, clean work areas, and respect for your home and schedule",
      "Accessibility fixture installation including ADA-compliant toilets, lever handles, and anti-scald devices",
      "Understanding of Sun City's recreation district infrastructure and shared water systems",
    ],
    uniqueSection: {
      type: "process",
      heading: "Senior-Focused Plumbing Assessment",
      steps: [
        {
          title: "Infrastructure Evaluation",
          desc: "We assess the age and condition of your supply lines, drain pipes, and water heater — identifying which original components are still serviceable and which are approaching failure based on material type and decades of Arizona hard water exposure.",
        },
        {
          title: "Accessibility Review",
          desc: "We evaluate your fixtures for aging-in-place safety — checking toilet height, faucet handle type, shower valve scald protection, and identifying opportunities to improve daily comfort and safety without major renovation.",
        },
        {
          title: "Prioritized Upgrade Plan",
          desc: "You receive a written plan that separates urgent repairs from recommended upgrades and future considerations — with transparent pricing so you can budget and schedule improvements at your own pace. No pressure, just honest guidance.",
        },
      ],
    },
  },

  "el-mirage-az": {
    headline: "El Mirage, AZ Plumber —",
    headlineAccent: "Affordable, Honest Plumbing Service",
    introParagraph:
      "El Mirage is a growing community with a unique housing mix — from established block-construction homes built in the 1970s and 1980s to manufactured homes, newer affordable housing developments, and rural properties on the city's edges. That diversity means El Mirage plumbing needs range from repiping aging stick-built homes to addressing the specialized connections and materials found in manufactured housing. We serve El Mirage with the same quality and honesty we bring to every West Valley community, at prices that respect working families' budgets.",
    localChallenges: {
      heading: "El Mirage Plumbing Challenges",
      items: [
        "Mixed-era housing stock — 1970s block homes with original copper or galvanized plumbing alongside 1990s–2000s manufactured homes with CPVC and polybutylene that require different repair approaches",
        "Manufactured home plumbing systems with non-standard fittings, belly-wrap drain configurations, and water heater installations that differ significantly from site-built home standards",
        "Water quality transitions as El Mirage's infrastructure expands — some older areas experience pressure fluctuations and water quality changes as new development connects to existing mains",
        "Arizona hard water at 15–25 gpg damaging fixtures and appliances across all housing types, with many El Mirage homes lacking any water treatment",
      ],
    },
    whyChooseUs: [
      "Transparent, honest pricing — we provide written estimates before starting work, no surprise charges",
      "Experience with both site-built and manufactured home plumbing systems and their different requirements",
      "Quick response from Tolleson — El Mirage is a short drive via the Loop 303 corridor",
      "Water treatment solutions sized and priced for El Mirage's specific water profile",
      "Serving El Mirage families, not just listing it as a coverage area — we know this community",
    ],
    uniqueSection: {
      type: "stats",
      items: [
        { value: "35K+", label: "El Mirage residents we're proud to serve" },
        { value: "15–25 gpg", label: "Typical water hardness in El Mirage" },
        { value: "10 min", label: "Our drive time via Loop 303" },
        { value: "1970s–Now", label: "Housing eras we service in El Mirage" },
      ],
    },
  },

  "sun-city-west-az": {
    seoTitle: "Plumber in Sun City West, AZ | Tru American Plumbing & Purification",
    seoDescription:
      "Trusted plumber in Sun City West, AZ. Veteran-owned, family-operated specialists serving the 55+ retirement community with repipes, water heaters, and water treatment built for aging West Valley homes. Call (480) 322-2567.",
    headline: "Plumber in Sun City West, AZ —",
    headlineAccent: "Retirement Community Plumbing Done Right",
    introParagraph:
      "Sun City West is the West Valley's premier age-restricted retirement community, built almost entirely between 1978 and the mid-1990s as the planned successor to original Sun City. Nearly four decades on, that means thousands of slab-on-grade homes are now hitting the age where original copper supply lines, builder-grade water heaters, and first-generation polybutylene runs all start failing at once. Add in some of the hardest municipal water in Arizona — drawn largely from the EPCOR groundwater system serving the Sun City West / Youngtown corridor — and you have a community where smart, senior-friendly plumbing service genuinely matters. We work west of R.H. Johnson Boulevard, around Stardust, Beardsley Park, and out toward the Grand Avenue corridor, every week.",
    localChallenges: {
      heading: "Plumbing Issues Specific to Sun City West",
      items: [
        "Late-1970s through 1990s copper and polybutylene supply lines now well past their reliable service window — pinhole leaks behind drywall and under slabs are the most common call we get out here",
        "Builder-installed 40–50 gallon water heaters from the second-owner era reaching 12–15+ years, often in interior closets where a tank rupture damages flooring and drywall fast",
        "EPCOR-supplied groundwater that runs consistently hard (typically 18–25 grains per gallon) — accelerating scale buildup in tankless units, fixtures, and dishwasher solenoids",
        "Aging cast iron and ABS drain stacks original to construction, plus mature ash and palm root intrusion into shared sewer laterals throughout the older recreation-center neighborhoods",
        "Aging-in-place retrofits: comfort-height ADA toilets, lever-handle faucets, thermostatic anti-scald shower valves, and grab-bar-compatible valve placement that respects existing tile and slab routing",
      ],
    },
    whyChooseUs: [
      "We specialize in 1978–1995 slab-on-grade construction — the exact era and build style that defines Sun City West",
      "Repipe specialists for copper-to-PEX and polybutylene replacement, with minimally invasive routing that protects existing finishes",
      "Senior-friendly service: clear written estimates, no high-pressure sales, clean drop cloths, shoe covers, and respect for your home and your schedule",
      "ADA-compliant fixture installation and accessibility retrofits done in a single visit when possible",
      "Familiar with Sun City West RCSCW guidelines and the placement rules around exterior water softeners, RO drains, and visible equipment",
    ],
    uniqueSection: {
      type: "stats",
      items: [
        { value: "1978", label: "Year Sun City West was founded" },
        { value: "18–25 gpg", label: "Typical EPCOR water hardness here" },
        { value: "30–45 yrs", label: "Age of most original supply lines" },
        { value: "20 min", label: "Drive time from our Tolleson HQ" },
      ],
    },
  },
};
